"use client";
import { createContext, useContext, useMemo } from "react";
import { jsx } from "react/jsx-runtime";
//#region src/utils/merge-refs.ts
/**
* Assign a value to a single ref, whether it's a callback ref or an object ref.
* Call this from a ref callback / effect (never during render) to forward a
* consumer ref to a node.
*/
function assignRef(ref, value) {
	if (!ref) return;
	if (typeof ref === "function") ref(value);
	else ref.current = value;
}
/**
* Compose multiple refs into a single ref callback. Each provided ref —
* callback or object — receives the same instance. Used to attach an extra ref
* to a node without clobbering a consumer ref that a component is already
* threading through its `forwardRef`.
*/
function mergeRefs(...refs) {
	return (instance) => {
		for (const ref of refs) assignRef(ref, instance);
	};
}
//#endregion
//#region src/utils/atoms-provider.tsx
var noopTrackJourneyEvent = () => {};
/**
* No-op default so an un-wrapped tree never crashes — the fail-safe property.
* Omitting the provider (or its `useObservability`) silently disables telemetry.
*/
var defaultAnalyticsContext = { useObservability: () => ({ trackJourneyEvent: noopTrackJourneyEvent }) };
var AnalyticsContext = createContext(defaultAnalyticsContext);
/** Read the analytics seam. Intended for `useObservabilitySafe`, not components directly. */
function useAnalyticsContext() {
	return useContext(AnalyticsContext);
}
/**
* Wrap an app to inject an observability implementation into atoms components.
*
* @example
* ```tsx
* import { AtomsProvider } from '@opendoor/atoms/utils';
* import { globalObservability } from '@opendoor/observability';
*
* const useObservability = () => ({
*   trackJourneyEvent: globalObservability.trackJourneyEvent,
* });
*
* <AtomsProvider useObservability={useObservability}>{app}</AtomsProvider>
* ```
*/
function AtomsProvider({ useObservability, children }) {
	const value = useMemo(() => ({ useObservability: useObservability ?? defaultAnalyticsContext.useObservability }), [useObservability]);
	return /* @__PURE__ */ jsx(AnalyticsContext.Provider, {
		value,
		children
	});
}
//#endregion
export { mergeRefs as i, useAnalyticsContext as n, assignRef as r, AtomsProvider as t };

